import React, { useState, useEffect, useMemo } from "react";
import { db, getCollectionName } from "./firebase";
import { collection, onSnapshot, doc, deleteDoc } from "./firestore-compat";
import { supabase } from "./supabase";

const ONLINE_THRESHOLD_MS = 30 * 60 * 1000; // sessoes ativas nos ultimos 30min
const VERY_RECENT_MS = 5 * 60 * 1000;

function formatDuration(seconds) {
  if (seconds < 60) return `${Math.floor(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}min`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h${m > 0 ? ` ${m}min` : ""}`;
}

function formatTime(isoString) {
  if (!isoString) return "-";
  return new Date(isoString).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

async function getAuthHeader() {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function OnlineUsers() {
  const [presence, setPresence] = useState([]);
  const [revoking, setRevoking] = useState(null);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [now, setNow] = useState(Date.now());

  // Le a presenca em tempo real (heartbeat gravado pelo proprio app enquanto o usuario esta com a tela aberta)
  useEffect(() => {
    const col = getCollectionName('user_presence');
    const unsub = onSnapshot(collection(db, col), snap => {
      setPresence(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, err => setError("Nao foi possivel carregar as sessoes: " + err.message));
    return () => unsub();
  }, []);

  // Atualiza o relogio a cada 30s para recalcular "tempo online" e reavaliar a janela de 30min
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(interval);
  }, []);

  const sessions = useMemo(() => {
    return presence
      .filter(p => p.lastSeen && (now - new Date(p.lastSeen).getTime()) < ONLINE_THRESHOLD_MS)
      .sort((a, b) => new Date(b.lastSeen) - new Date(a.lastSeen));
  }, [presence, now]);

  // Deslogar um usuario
  const handleRevokeSession = async (userId, userEmail) => {
    if (!window.confirm(`Deseja forcar o logout de "${userEmail}"?\n\nEle sera desconectado imediatamente.`)) return;
    setRevoking(userId);
    setError("");
    setSuccessMsg("");
    try {
      const headers = await getAuthHeader();
      const res = await fetch("/api/revoke-session", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ userId, email: userEmail }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Erro ${res.status}`);
      }

      await deleteDoc(doc(db, getCollectionName('user_presence'), userId)).catch(() => {});
      setSuccessMsg(`${userEmail} foi deslogado com sucesso.`);
    } catch (err) {
      setError("Erro ao deslogar usuario: " + err.message);
    } finally {
      setRevoking(null);
    }
  };

  // Deslogar todos (exceto o usuario atual)
  const handleRevokeAll = async () => {
    const { data } = await supabase.auth.getUser();
    const meuEmail = String(data?.user?.email || '').toLowerCase();
    const others = sessions.filter(s => String(s.email || '').toLowerCase() !== meuEmail);

    if (others.length === 0) { alert("Nenhum outro usuario online."); return; }
    if (!window.confirm(`Deseja forcar o logout de TODOS os ${others.length} usuarios online?`)) return;

    setError("");
    setSuccessMsg("");

    try {
      const headers = await getAuthHeader();
      const res = await fetch("/api/revoke-session", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ emails: others.map(s => s.email).filter(Boolean), userIds: others.filter(s => !s.email).map(s => s.id) }),
      });

      const data2 = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data2.error || `Erro ${res.status}`);

      await Promise.all(others.map(s => deleteDoc(doc(db, getCollectionName('user_presence'), s.id)).catch(() => {})));
      setSuccessMsg(`${data2.revoked} usuario(s) deslogado(s) com sucesso.`);
    } catch (err) {
      setError("Erro ao deslogar todos: " + err.message);
    }
  };

  const cardStyle = {
    background: "#fff",
    borderRadius: "12px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    overflow: "hidden",
  };

  return (
    <div className="tab-content" style={{ padding: "24px" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "24px", flexWrap: "wrap", gap: "12px" }}>
        <div>
          <h3 style={{ margin: 0, color: "#1a1a2e", fontSize: "1.25rem", fontWeight: 700 }}>
            Usuarios Online
          </h3>
          <p style={{ margin: "4px 0 0", color: "#6b7280", fontSize: "0.85rem" }}>
            Sessoes ativas nos ultimos 30 minutos · Atualizacao em tempo real
          </p>
        </div>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          {sessions.length > 1 && (
            <button
              onClick={handleRevokeAll}
              style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", padding: "8px 16px", borderRadius: "8px", cursor: "pointer", fontWeight: 600, fontSize: "0.85rem" }}
            >
              Deslogar Todos
            </button>
          )}
        </div>
      </div>

      {/* Alertas */}
      {error && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", padding: "12px 16px", borderRadius: "8px", marginBottom: "16px", fontSize: "0.88rem" }}>
          {error}
        </div>
      )}
      {successMsg && (
        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#16a34a", padding: "12px 16px", borderRadius: "8px", marginBottom: "16px", fontSize: "0.88rem" }}>
          {successMsg}
        </div>
      )}

      {/* Contador */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "16px", marginBottom: "24px" }}>
        <div style={{ ...cardStyle, padding: "20px", textAlign: "center" }}>
          <div style={{ fontSize: "2.5rem", fontWeight: 800, color: "#10b981", lineHeight: 1 }}>{sessions.length}</div>
          <div style={{ color: "#6b7280", fontSize: "0.85rem", marginTop: "4px" }}>Online agora</div>
        </div>
        <div style={{ ...cardStyle, padding: "20px", textAlign: "center" }}>
          <div style={{ fontSize: "2.5rem", fontWeight: 800, color: "#6366f1", lineHeight: 1 }}>
            {sessions.filter(s => Math.floor((now - new Date(s.lastSeen).getTime()) / 1000) < VERY_RECENT_MS / 1000).length}
          </div>
          <div style={{ color: "#6b7280", fontSize: "0.85rem", marginTop: "4px" }}>Ativos (ultimos 5min)</div>
        </div>
      </div>

      {/* Lista */}
      {sessions.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: "center", padding: "48px", color: "#9ca3af" }}>
          <div style={{ fontSize: "2rem", marginBottom: "12px" }}>Nenhum usuario online no momento</div>
          <div style={{ fontSize: "0.85rem" }}>Usuarios com o sistema aberto nos ultimos 30 minutos aparecao aqui</div>
        </div>
      ) : (
        <div style={cardStyle}>
          {/* Cabecalho */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1.5fr 1fr 1fr 130px", backgroundColor: "#f0f4ff", padding: "10px 20px", fontWeight: 700, fontSize: "0.75rem", color: "#555", textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: "2px solid #e0e7ff" }}>
            <span>Usuario</span>
            <span>E-mail</span>
            <span>Ativo desde</span>
            <span>Tempo online</span>
            <span style={{ textAlign: "center" }}>Acao</span>
          </div>

          {/* Linhas */}
          {sessions.map((s, idx) => {
            const secOnline = Math.floor((now - new Date(s.lastSeen).getTime()) / 1000);
            const isVeryRecent = secOnline < VERY_RECENT_MS / 1000;
            const isBeingRevoked = revoking === s.id;

            return (
              <div
                key={s.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1.5fr 1fr 1fr 130px",
                  padding: "14px 20px",
                  alignItems: "center",
                  borderBottom: idx < sessions.length - 1 ? "1px solid #f3f4f6" : "none",
                  background: isBeingRevoked ? "#fef9f0" : "#fff",
                  transition: "background 0.2s",
                }}
              >
                {/* Nome + indicador */}
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <div style={{
                    width: "10px", height: "10px", borderRadius: "50%",
                    backgroundColor: isVeryRecent ? "#10b981" : "#f59e0b",
                    boxShadow: isVeryRecent ? "0 0 0 3px rgba(16,185,129,0.2)" : "none",
                    flexShrink: 0,
                    animation: isVeryRecent ? "pulse 2s infinite" : "none",
                  }} />
                  <span style={{ fontWeight: 600, color: "#1f2937", fontSize: "0.9rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {s.name}
                  </span>
                </div>

                {/* E-mail */}
                <span style={{ color: "#6b7280", fontSize: "0.85rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {s.email}
                </span>

                {/* Horario */}
                <span style={{ color: "#374151", fontSize: "0.88rem" }}>
                  {formatTime(s.lastSeen)}
                </span>

                {/* Tempo online */}
                <span style={{ fontSize: "0.85rem", color: isVeryRecent ? "#10b981" : "#f59e0b", fontWeight: 600 }}>
                  {formatDuration(secOnline)}
                </span>

                {/* Botao deslogar */}
                <div style={{ textAlign: "center" }}>
                  <button
                    onClick={() => handleRevokeSession(s.id, s.email)}
                    disabled={isBeingRevoked || !!revoking}
                    style={{
                      background: isBeingRevoked ? "#f3f4f6" : "#fef2f2",
                      border: "1px solid #fecaca",
                      color: isBeingRevoked ? "#9ca3af" : "#dc2626",
                      padding: "6px 14px",
                      borderRadius: "6px",
                      cursor: isBeingRevoked ? "not-allowed" : "pointer",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      transition: "all 0.2s",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isBeingRevoked ? "Deslogando..." : "Deslogar"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Legenda */}
      <div style={{ marginTop: "20px", padding: "14px 16px", background: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "0.82rem", color: "#64748b", lineHeight: 1.6 }}>
        <strong>Indicador verde</strong> = ativo nos ultimos 5 min &nbsp;·&nbsp;
        <strong>Indicador amarelo</strong> = ativo entre 5-30 min &nbsp;·&nbsp;
        Ao clicar em Deslogar, o token e revogado imediatamente e o usuario sera redirecionado para o login.
      </div>
    </div>
  );
}
